/**
 * PreMeetingBriefV2Workbench — overlay equivalent of the legacy
 * /os/agents?action=pmb_v2 route. Mounts a portal at document.body so
 * clicking "Generate Pre-Meeting Brief" anywhere in the app opens an
 * Ask-OrbitShift chat + PMB v2 widget on top of the current page without
 * any navigation.
 *
 * Layout (matches sales-pitch workbench gestalt):
 *
 *   ┌────────────────┬────────────────────────┬───────────────────────┐
 *   │ Backdrop       │ Display pane (widget)  │ AskOS chat (input +   │
 *   │ (click closes) │ — mounts once the SSE  │ messages, scrolls)    │
 *   │                │   ui_component frame   │                       │
 *   │                │   sets showSplitter    │                       │
 *   └────────────────┴────────────────────────┴───────────────────────┘
 *
 * Plumbing:
 *   1. createOsAgentsSession()  — get an ASKOS session id for the chat
 *   2. createPmbV2Brief()       — pre-create the pm_brief row keyed by
 *                                 that session id
 *   3. sendMessageRef.current({...}) — auto-fire the seeded query with
 *      transferToAgent + pmbBriefId + pmbContext (matches the payload
 *      the now-deleted /os/agents?action=pmb_v2 auto-fire produced).
 *
 * Why a self-contained component (vs. extracting a generic AgentWorkbench
 * helper): on main, no equivalent exists yet; building one would diverge
 * from active development branches. This file is small and self-explanatory
 * — once an AgentWorkbench lands on main, this can collapse onto it.
 */
import { Loader, X } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { createOsAgentsSession } from "~/api/os.agents"
import { createPmbV2Brief } from "~/api/pre-meeting-v2"
import AgentIdMessagesV2 from "~/containers/os-agents/agent-id-messages-v2"
import AgentRightPanel from "~/containers/os-agents/agent-right-panel"
import AgentSearchV2 from "~/containers/os-agents/agent-search-v2"
import useGlobalStore from "~/store/global"
import useOsAgentsIdStore from "~/store/os.agents_.$id"
import usePmbV2WorkbenchStore, {
  type PmbV2WorkbenchInput
} from "~/store/pmb-v2-workbench"

export default function PreMeetingBriefV2Workbench() {
  const isOpen = usePmbV2WorkbenchStore((s) => s.isOpen)
  const input = usePmbV2WorkbenchStore((s) => s.input)
  const close = usePmbV2WorkbenchStore((s) => s.close)

  // Splitter is the global "right panel is open" signal — when the SSE
  // handler in agent-search-v2 sees a PMB v2 ui_component frame, it calls
  // setShowSplitter(content) and the right panel renders the widget.
  // We read it to decide whether to show the display pane.
  const showSplitter = useOsAgentsIdStore((s) => s.showSplitter)
  const setShowSplitter = useOsAgentsIdStore((s) => s.setShowSplitter)

  if (!isOpen || !input) return null
  // Force a full remount when the user clicks "Pre-meeting brief" for a
  // different person while a workbench is already open. Without this key,
  // the inner component's sessionId state would update to the new
  // session but autoFiredRef stays true from the previous person — the
  // chat panel re-renders with the new (empty) session and never
  // auto-dispatches, producing a blank chat. Remount is the cleanest
  // reset: new refs, new state, prior chat history stays cached in the
  // global messages store so re-opening that person resumes it.
  const instanceKey = `${input.linkedinUrl}::${input.opportunityId ?? ""}`
  return (
    <PreMeetingBriefV2WorkbenchInner
      key={instanceKey}
      input={input}
      close={close}
      showSplitter={showSplitter}
      setShowSplitter={setShowSplitter}
    />
  )
}

interface InnerProps {
  input: PmbV2WorkbenchInput
  close: () => void
  showSplitter: any
  setShowSplitter: (v: any) => void
}

function PreMeetingBriefV2WorkbenchInner({
  input,
  close,
  showSplitter,
  setShowSplitter
}: InnerProps) {
  const tokenInfo = useGlobalStore((s) => s.tokenInfo)
  const sendMessageRef = useRef<any>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const autoFiredRef = useRef(false)

  // Session storage key keyed by linkedin + opportunity so re-opening the
  // workbench for the same person/opp reuses the prior session (and its
  // chat history) instead of creating a fresh one each time.
  const storageKey = useMemo(() => {
    const personKey = simpleHash(input.linkedinUrl)
    const oppKey = input.opportunityId
      ? `-opp${String(input.opportunityId)}`
      : ""
    return `workbench-session-pmb-${personKey}${oppKey}`
  }, [input.linkedinUrl, input.opportunityId])

  // Reset cross-brief bleed-through: if a previous opening left showSplitter
  // pointing at someone else's widget (different brief_id), clear it before
  // the new SSE stream populates a fresh one.
  useEffect(() => {
    setShowSplitter(null)
    return () => {
      // Closing the workbench should also drop the splitter so when the
      // user opens a non-PMB agent surface later the panel starts clean.
      setShowSplitter(null)
    }
  }, [setShowSplitter, input.linkedinUrl, input.opportunityId])

  // ── Session bootstrap ─────────────────────────────────────────────
  // Mirror sales-pitch's workbench-session-${...}-${agent} pattern:
  // a sessionStorage cache so re-opening the SAME (person, opp) reuses the
  // prior ASKOS session (and its message history in the store).
  useEffect(() => {
    if (!tokenInfo) return
    const cached =
      typeof window !== "undefined" ? sessionStorage.getItem(storageKey) : null
    if (cached) {
      setSessionId(cached)
      return
    }
    let cancelled = false
    createOsAgentsSession(tokenInfo)
      .then((res) => {
        if (cancelled) return
        const sid: string | undefined = res?.data?.data?.session_id
        if (!sid) {
          setSessionError("Couldn't start a new chat session.")
          return
        }
        try {
          sessionStorage.setItem(storageKey, sid)
        } catch {
          /* private mode etc. — non-fatal, just lose re-open caching */
        }
        setSessionId(sid)
      })
      .catch(() => {
        if (cancelled) return
        setSessionError("Couldn't start a new chat session.")
      })
    return () => {
      cancelled = true
    }
  }, [tokenInfo, storageKey])

  // ── Auto-fire on first open ───────────────────────────────────────
  // Once we have a sessionId AND AgentSearchV2 has populated
  // sendMessageRef, we pre-create the pm_brief row and dispatch the
  // seeded "Generate a pre-meeting brief for {linkedinUrl}" message
  // with the full PMB hand-off blob. This payload shape is identical to
  // what the now-deleted /os/agents?action=pmb_v2 auto-fire built — the
  // py-side tool reads pmbBriefId + pmbContext.brief_id off the message.
  //
  // Guarded by:
  //   - autoFiredRef       — one fire per workbench mount
  //   - sessionMessages    — skip if the session already has messages
  //                          (re-open of the same person/opp resumes
  //                           without re-prompting the agent)
  useEffect(() => {
    if (!sessionId || !tokenInfo) return
    if (autoFiredRef.current) return
    const existingMessages = useOsAgentsIdStore.getState().messages?.[sessionId]
    if (existingMessages && Object.keys(existingMessages).length > 0) {
      autoFiredRef.current = true
      return
    }
    let cancelled = false
    let attempts = 0
    const poll = setInterval(async () => {
      attempts++
      if (cancelled) {
        clearInterval(poll)
        return
      }
      const fn = sendMessageRef.current
      if (!fn) {
        if (attempts >= 40) clearInterval(poll)
        return
      }
      clearInterval(poll)
      autoFiredRef.current = true

      let briefId = ""
      try {
        const resp = await createPmbV2Brief(tokenInfo, {
          session_id_v2: sessionId,
          linkedin_url: input.linkedinUrl,
          account_id: input.accountId ?? null,
          apollo_id: input.apolloId ?? null,
          opportunity_id: input.opportunityId ?? null
        })
        briefId = (resp as any)?.data?.data?.brief_id?.toString() ?? ""
      } catch {
        // Best-effort — fall through to send the message anyway; the
        // agent's tool will surface a "brief_id missing" error in chat
        // rather than crashing the workbench.
      }

      if (cancelled) return
      const query = `Generate a pre-meeting brief for ${input.linkedinUrl}`
      try {
        fn({
          agentQuery: query,
          accountId:
            input.accountId != null && input.accountId !== ""
              ? String(input.accountId)
              : "",
          transferToAgent: "GeneratePreMeetingBriefAgentV2",
          pmbBriefId: briefId,
          pmbContext: {
            brief_id: briefId,
            linkedin_url: input.linkedinUrl,
            account_id: input.accountId ?? undefined,
            apollo_id: input.apolloId ?? undefined,
            opportunity_id: input.opportunityId ?? undefined,
            mode: "generate"
          }
        })
      } catch {
        // auto-fire dispatch failed
      }
    }, 50)
    return () => {
      cancelled = true
      clearInterval(poll)
    }
  }, [
    sessionId,
    tokenInfo,
    input.linkedinUrl,
    input.accountId,
    input.apolloId,
    input.opportunityId
  ])

  // ── ESC to close ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [close])

  const hasSplitter = !!showSplitter
  const title = useMemo(() => {
    const name = input.personName?.trim()
    const account = input.accountName?.trim()
    if (name && account) return `Pre-Meeting Brief · ${name} / ${account}`
    if (name) return `Pre-Meeting Brief · ${name}`
    return "Pre-Meeting Brief"
  }, [input.personName, input.accountName])

  return createPortal(
    // pointer-events-none on the parent lets clicks/scrolls in the empty
    // left area pass through to the underlying page — user can still
    // interact with the page they launched from while the workbench is
    // open. Each interactive pane re-enables pointer events for itself.
    <div className="fixed inset-0 z-[9999] flex justify-end pointer-events-none">
      {/* Display pane — mounts only after the BE emits a ui_component
          frame and setShowSplitter is populated. AgentRightPanel already
          knows how to render PreMeetingBriefV2Widget for PMB v2 frames.
          Background + text colors mirror the widget itself (dark-3 +
          dark-6) so there's no visible seam at the title-bar boundary. */}
      {hasSplitter && (
        <div className="w-[50%] h-full bg-white dark:bg-dark-3 text-dark-2 dark:text-dark-6 border-l border-light-border dark:border-border flex flex-col overflow-hidden pointer-events-auto shadow-2xl">
          <div className="flex items-center justify-between px-4 py-3 border-b border-light-border dark:border-border shrink-0">
            <span className="text-13 font-semibold truncate pr-2">{title}</span>
            {/* No close button here — the widget below has its own X
                (clears the display pane via setShowSplitter), and the
                chat-panel header has the workbench-close X. Two paths
                with clear semantics, no redundant button. */}
          </div>
          <div className="flex-1 overflow-y-auto min-h-0">
            <AgentRightPanel sendMessageRef={sendMessageRef} />
          </div>
        </div>
      )}

      {/* AskOS chat — fixed 30% width on the right (was 25%; widened so
          the clarification picker pills, picker controls, and code-style
          chat content breathe in the narrow column). */}
      <div
        className={`h-full flex flex-col overflow-hidden bg-white dark:bg-dark-3 text-dark-2 dark:text-dark-6 pointer-events-auto shadow-2xl transition-[width] duration-200 ${
          hasSplitter
            ? "w-[30%] border-l border-light-border dark:border-border"
            : "w-[30%]"
        }`}
      >
        <WorkbenchChatHeader onClose={close} />
        {sessionId ? (
          <>
            <div className="flex-1 overflow-y-auto min-h-0">
              <AgentIdMessagesV2
                sendMessageRef={sendMessageRef}
                sessionIdOverride={sessionId}
                mode="ask"
                embedded
              />
            </div>
            <AgentSearchV2
              sessionIdOverride={sessionId}
              accountIdOverride={
                input.accountId != null && input.accountId !== ""
                  ? String(input.accountId)
                  : undefined
              }
              onSubmitRef={sendMessageRef}
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            {sessionError ? (
              <p className="text-sm text-red-500 px-6 text-center">
                {sessionError}
              </p>
            ) : (
              <Loader
                className="text-primary dark:text-primary-light animate-spin"
                size={20}
                aria-label="Starting Ask OrbitShift chat"
              />
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

function WorkbenchChatHeader({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex items-center justify-between py-3 px-4 shrink-0 border-b border-light-border dark:border-border">
      <div className="flex items-center gap-2 font-medium text-sm text-dark-2 dark:text-dark-6">
        <svg
          preserveAspectRatio="xMidYMid meet"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5 shrink-0"
          role="img"
          aria-label="Ask OrbitShift"
        >
          <title>Ask OrbitShift</title>
          <path
            d="M20.616 10.835a14.147 14.147 0 01-4.45-3.001 14.111 14.111 0 01-3.678-6.452.503.503 0 00-.975 0 14.134 14.134 0 01-3.679 6.452 14.155 14.155 0 01-4.45 3.001c-.65.28-1.318.505-2.002.678a.502.502 0 000 .975c.684.172 1.35.397 2.002.677a14.147 14.147 0 014.45 3.001 14.112 14.112 0 013.679 6.453.502.502 0 00.975 0c.172-.685.397-1.351.677-2.003a14.145 14.145 0 013.001-4.45 14.113 14.113 0 016.453-3.678.503.503 0 000-.975 13.245 13.245 0 01-2.003-.678z"
            fill="#6c5ce7"
          />
        </svg>
        <h3>Ask OrbitShift</h3>
        <span className="text-red-400 text-xs">(Beta)</span>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close pre-meeting brief workbench"
        className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors"
      >
        <X size={16} />
      </button>
    </div>
  )
}

// Tiny deterministic hash for the storageKey. Good enough — collisions
// across the same user's linkedin URLs are vanishingly unlikely and the
// only consequence of a collision is a shared chat session, which is the
// existing behavior anyway for the same (person, opp) pair.
function simpleHash(s: string): string {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i)
    h |= 0
  }
  return Math.abs(h).toString(36)
}
